/**
 * Utilitário para traduzir códigos de erro do Firebase Authentication para português
 */

export interface FirebaseError {
  code: string;
  message: string;
}

/**
 * Mapeamento de códigos de erro do Firebase para mensagens em português
 */
const FIREBASE_ERROR_MESSAGES: Record<string, string> = {
  // Erros de credenciais
  'auth/invalid-credential': 'As credenciais fornecidas são inválidas ou expiraram.',
  'auth/wrong-password': 'A senha fornecida está incorreta.',
  'auth/user-not-found': 'Não há registro de usuário correspondente ao identificador fornecido.',
  'auth/invalid-email': 'O endereço de e-mail fornecido é inválido.',
  'auth/user-disabled': 'A conta do usuário foi desativada por um administrador.',
  
  // Erros de cadastro
  'auth/email-already-exists': 'O e-mail fornecido já está em uso por outro usuário.',
  'auth/weak-password': 'A senha fornecida é muito fraca. Escolha uma senha mais forte.',
  'auth/operation-not-allowed': 'O provedor de login correspondente não está habilitado para este projeto.',
  
  // Erros de rede e sistema
  'auth/network-request-failed': 'Ocorreu um erro de rede. Verifique sua conexão e tente novamente.',
  'auth/too-many-requests': 'O acesso a esta conta foi temporariamente desativado devido a muitas tentativas de login malsucedidas. Tente novamente mais tarde.',
  'auth/quota-exceeded': 'A cota de solicitações foi excedida. Tente novamente mais tarde.',
  'auth/app-deleted': 'Esta instância do FirebaseApp foi excluída.',
  'auth/app-not-authorized': 'Esta aplicação não está autorizada a usar Firebase Authentication.',
  'auth/argument-error': 'Um argumento inválido foi fornecido.',
  'auth/invalid-api-key': 'A chave da API fornecida é inválida.',
  'auth/invalid-user-token': 'O token do usuário fornecido é inválido.',
  'auth/requires-recent-login': 'Esta operação é sensível e requer autenticação recente. Faça login novamente antes de tentar novamente.',
  'auth/unauthorized-domain': 'Este domínio não está autorizado para operações do Firebase.',
  'auth/user-mismatch': 'As credenciais fornecidas não correspondem ao usuário anteriormente registrado.',
  'auth/user-token-expired': 'O token do usuário fornecido expirou.',
  'auth/web-storage-unsupported': 'O armazenamento web não é suportado ou está desabilitado.',
  
  // Erros de verificação de email
  'auth/invalid-action-code': 'O código de ação fornecido é inválido.',
  'auth/expired-action-code': 'O código de ação fornecido expirou.',
  'auth/invalid-verification-code': 'O código de verificação fornecido é inválido.',
  'auth/invalid-verification-id': 'O ID de verificação fornecido é inválido.',
  'auth/missing-verification-code': 'O código de verificação é obrigatório.',
  'auth/missing-verification-id': 'O ID de verificação é obrigatório.',
  'auth/code-expired': 'O código expirou. Solicite um novo.',
  
  // Erros de recuperação de senha
  'auth/invalid-continue-uri': 'A URL de continuação fornecida é inválida.',
  'auth/missing-continue-uri': 'A URL de continuação é obrigatória.',
  'auth/unauthorized-continue-uri': 'A URL de continuação fornecida não está autorizada.',
  
  // Erros de captcha
  'auth/captcha-check-failed': 'A verificação do reCAPTCHA falhou.',
  'auth/invalid-app-credential':
    'Falha na verificação reCAPTCHA para SMS. Acesse o app por http://127.0.0.1:5173 (não localhost), adicione 127.0.0.1 em Authentication → Settings → Authorized domains e confira Phone + reCAPTCHA no Firebase Console.',
  'auth/internal-error': 'Erro interno na verificação. Feche o modal e tente novamente.',
  'auth/invalid-phone-number': 'O número de telefone fornecido é inválido.',
  'auth/missing-phone-number': 'O número de telefone é obrigatório.',
  
  // Erros de provedor
  'auth/account-exists-with-different-credential': 'Já existe uma conta com o mesmo endereço de e-mail, mas com credenciais diferentes.',
  'auth/credential-already-in-use': 'Esta credencial já está associada a uma conta de usuário diferente.',
  'auth/popup-blocked': 'O popup foi bloqueado pelo navegador.',
  'auth/popup-closed-by-user': 'O popup foi fechado pelo usuário antes de completar a operação.',
  'auth/provider-already-linked': 'O provedor já está vinculado a esta conta.',
  'auth/no-such-provider': 'O provedor não está vinculado a esta conta.',
  
  // Erros de timeout
  'auth/timeout': 'A operação expirou.',
  
  'storage/unauthorized': 'Sem permissão para enviar a foto. Confira as regras do Firebase Storage.',
  'storage/unauthenticated': 'Faça login para enviar a foto.',
  'storage/canceled': 'O envio da foto foi cancelado.',
  'storage/retry-limit-exceeded': 'Falha de rede ao enviar a foto. Tente novamente.',
  'storage/quota-exceeded': 'O armazenamento de fotos atingiu o limite do projeto.',
  'storage/unknown': 'Não foi possível enviar a foto. Tente novamente.',

  // Erros de configuração
  'auth/configuration-not-found': 'A configuração necessária não foi encontrada.',
  'auth/duplicate-credential': 'A credencial fornecida já está associada a uma conta de usuário diferente.',
  'auth/email-change-needs-verification': 'A alteração de e-mail requer verificação.',
  'auth/invalid-message-payload': 'O payload da mensagem de e-mail é inválido.',
  'auth/invalid-recipient-email': 'O e-mail do destinatário é inválido.',
  'auth/invalid-sender': 'O remetente do e-mail é inválido.',
  'auth/missing-email': 'O endereço de e-mail é obrigatório.',
};

/**
 * Traduz um código de erro do Firebase para uma mensagem em português
 */
export function translateFirebaseError(errorCode: string): string {
  return FIREBASE_ERROR_MESSAGES[errorCode] || 'Ocorreu um erro inesperado. Tente novamente.';
}

/**
 * Traduz um erro completo do Firebase para uma mensagem em português
 */
export function translateFirebaseErrorObject(error: any): string {
  const nestedMessage = error?.error?.message ?? error?.customData?.message;
  if (nestedMessage === 'BILLING_NOT_ENABLED') {
    return 'Verificação por SMS exige plano Blaze no Firebase (billing ativo).';
  }
  if (nestedMessage === 'INVALID_APP_CREDENTIAL') {
    return translateFirebaseError('auth/invalid-app-credential');
  }

  if (error?.code) {
    if (error.code === 400 && error.message === 'BILLING_NOT_ENABLED') {
      return 'Verificação por SMS exige plano Blaze no Firebase (billing ativo).';
    }
    if (error.code === 400 && error.message === 'INVALID_APP_CREDENTIAL') {
      return translateFirebaseError('auth/invalid-app-credential');
    }
    return translateFirebaseError(error.code);
  }

  if (error?.message) {
    if (error.message.includes('BILLING_NOT_ENABLED')) {
      return 'Verificação por SMS exige plano Blaze no Firebase (billing ativo).';
    }
    if (error.message.includes('INVALID_APP_CREDENTIAL')) {
      return translateFirebaseError('auth/invalid-app-credential');
    }
    if (error.message.includes('reCAPTCHA has already been rendered')) {
      return 'Erro temporário do reCAPTCHA. Feche o modal e tente enviar o código novamente.';
    }

    const codeMatch = error.message.match(/\(([^)]+)\)/);
    if (codeMatch) {
      return translateFirebaseError(codeMatch[1]);
    }

    return error.message;
  }

  return 'Ocorreu um erro inesperado. Tente novamente.';
}

/**
 * Verifica se um erro é do Firebase Authentication
 */
export function isFirebaseAuthError(error: any): boolean {
  return error?.code?.startsWith('auth/') || 
         (error?.message && error.message.includes('Firebase: Error'));
}

/**
 * Extrai o código de erro de uma mensagem do Firebase
 */
export function extractFirebaseErrorCode(message: string): string | null {
  const match = message.match(/Firebase: Error \(([^)]+)\)/);
  return match ? match[1] : null;
}

